'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Text,
  Badge,
  IconButton,
  Tooltip,
  Splitter,
  Menu,
  HttpEditor,
  Checkbox,
  Input,
  renderHttpBadge,
  toasterApi,
} from '@terra/ui-design-system';
import {
  ArrowRight02Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  MoreHorizontalIcon,
  PlayIcon,
  SquareIcon,
  Undo02Icon,
  UserIcon,
} from '@terra/ui-icons';
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useTranslations } from 'next-intl';
import {
  SelectHttpContextForChatDialog,
  type SelectHttpContextForChatDialogState,
} from '../dialogs/SelectHttpContextForChatDialog';
import { ScriptPresetsManager } from '../replay/ScriptPresetsManager';
import { useLayoutStore } from '../../store/layoutStore';
import { useProxyStore } from '../../store/proxy-store';
import {
  formatTargetHostHeader,
  getHostHeaderValue,
  parseHostHeaderTargetForProtocol,
  replaceHostHeader,
} from '../../utils/simpleReplayTargetSync.utils';
import { ReplayRequestEditor } from '../replay/ReplayRequestEditor';
import type { ActiveCredential } from '../replay/CredentialToolbarDropdown';
import { useChatStore } from '../../store/chatStore';
import {
  createHttpRequestMention,
  createHttpResponseMention,
} from '../../utils/httpMentionUtils';
import { useReplayRequest } from '../../hooks/useReplayRequest';
import { useHttpReplay } from '../../hooks/useHttpReplay';
import { formatHttpResponseRaw } from '../../utils/formatHttpForPanel';
import {
  parseRequestFromText,
  type ConnectionTarget,
} from '../../utils/httpFormatter';
import { PRODUCT_DISPLAY_NAME } from '@terra/shared-utils';
import type {
  HarGrpcBodyView,
  StoredHarDisplayEntry,
} from '@terra/shared-utils';
import type { WebGrpcDecodedData } from '@terra/shared-schemas-tracker';
import { resolveReplayResponseDisplayContent } from '../../utils/replayGrpcDisplay.utils';
import { useReplayGrpcBodyView } from '../../hooks/useReplayGrpcBodyView';
import { useReplayGrpcResponseDecode } from '../../hooks/useReplayGrpcResponseDecode';

type SimpleReplayTabProps = {
  sessionId: string;
};

function PortInput({
  port,
  onCommit,
}: {
  port: number;
  onCommit: (port: number) => void;
}) {
  const [localPort, setLocalPort] = useState(String(port));

  useEffect(() => {
    setLocalPort(String(port));
  }, [port]);

  return (
    <Input
      size="xs"
      type="number"
      value={localPort}
      onChange={(e) => setLocalPort(e.target.value)}
      onBlur={() => {
        const parsed = Number(localPort);
        const clamped = Math.max(1, Math.min(65535, parsed));

        onCommit(Number.isFinite(clamped) ? clamped : port);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        }
      }}
      w="60px"
      minW="60px"
      h="24px"
      fontFamily="mono"
      fontSize="xs"
      aria-label="Port"
    />
  );
}

function getPlatformModifierKey(): string {
  if (typeof navigator !== 'undefined') {
    return navigator.platform?.startsWith('Mac') ? 'Cmd' : 'Ctrl';
  }

  return 'Ctrl';
}

export function SimpleReplayTab({ sessionId }: SimpleReplayTabProps) {
  const tScripts = useTranslations('replay.scripts');
  const tSessions = useTranslations('replay.sessions');
  const {
    session,
    isLoading,
    preRequestSandboxStatus,
    setActivePresetId,
    handleReplay,
    handleRunAllCredentials,
    handleReset,
    handleFollowRedirect,
    handleNavigateResponse,
    updateSessionRequest,
    updateSessionPreserveContentLength,
    updateSessionConnectionTarget,
    updateSessionActiveCredential,
    updateSessionAutoFollowRedirects,
    updateSessionSyncTargetWithHostHeader,
  } = useReplayRequest({ sessionId });
  const replaySplitterSizes = useLayoutStore(
    (state) => state.replaySplitterSizes,
  );
  const isHttpsProxySupportEnabled = useProxyStore(
    (state) => state.settings.enableHttps,
  );
  const setReplaySplitterSizes = useLayoutStore(
    (state) => state.setReplaySplitterSizes,
  );
  const appendToInput = useChatStore((s) => s.appendToInput);
  const sendHttpMentionsToChat = useChatStore(
    (store) => store.sendHttpMentionsToChat,
  );
  const { openInReplay } = useHttpReplay();
  const [httpContextDialogState, setHttpContextDialogState] =
    useState<SelectHttpContextForChatDialogState | null>(null);
  const handleHttpContextDialogClose = useCallback(() => {
    setHttpContextDialogState(null);
  }, []);
  const { bodyView, grpcHarDisplay, editorProps } = useReplayGrpcBodyView({
    sessionId,
    session,
  });
  const currentReplayResponse =
    session && session.currentResponseIndex >= 0
      ? (session.responseHistory[session.currentResponseIndex] ?? null)
      : null;

  useReplayGrpcResponseDecode({
    sessionId,
    session,
    response: currentReplayResponse,
    responseIndex: session?.currentResponseIndex ?? -1,
  });

  const responseSessionRequest = session?.request;
  const responseSessionIndex = session?.currentResponseIndex;
  const responsePanelSession = useMemo(
    () =>
      responseSessionRequest
        ? {
            request: responseSessionRequest,
            currentResponseIndex: responseSessionIndex ?? 0,
            protocol: session?.protocol,
          }
        : null,
    [responseSessionRequest, responseSessionIndex, session?.protocol],
  );
  const handleRequestChange = useCallback(
    (data: string) => {
      updateSessionRequest(sessionId, data);
      const s = sessionRef.current;

      if (!s?.syncTargetWithHostHeader) {
        return;
      }

      const hostHeader = getHostHeaderValue(data);

      if (!hostHeader) {
        return;
      }

      const target = parseHostHeaderTargetForProtocol({
        hostHeader,
        https: s.connectionTarget.https,
      });

      if (target) {
        updateSessionConnectionTarget(sessionId, target);
      }
    },
    [updateSessionRequest, updateSessionConnectionTarget, sessionId],
  );
  const handleCredentialApplied = useCallback(
    (credential: ActiveCredential) =>
      updateSessionActiveCredential(sessionId, credential),
    [updateSessionActiveCredential, sessionId],
  );
  const sessionRef = useRef(session);

  sessionRef.current = session;
  const handleRunAllCredentialsRef = useRef(handleRunAllCredentials);

  handleRunAllCredentialsRef.current = handleRunAllCredentials;
  const stableHandleRunAllCredentials = useCallback(
    (
      credentialsWithRequests: Parameters<typeof handleRunAllCredentials>[0],
    ) => {
      handleRunAllCredentialsRef.current(credentialsWithRequests);
    },
    [],
  );
  const handleRequestSendToChat = useCallback(
    (content: string, selectedText?: string) => {
      const s = sessionRef.current;

      if (!s) return;
      if (selectedText) {
        appendToInput(selectedText);

        return;
      }

      const parsed = parseRequestFromText({
        text: s.requestData,
        originalUrl: s.request.url,
      });

      if (!parsed) {
        toasterApi.error({
          title: 'Failed to send to chat',
          description: 'Could not parse the request. Check the request format.',
        });

        return;
      }

      const mention = createHttpRequestMention({
        id: `replay-request-${sessionId}`,
        method: parsed.method,
        url: parsed.url,
        headers: parsed.headers,
        body: parsed.body,
        httpVersion: parsed.httpVersion,
      });
      const responseItem =
        s.responseHistory[s.currentResponseIndex] ??
        s.responseHistory[s.responseHistory.length - 1];

      if (responseItem && !responseItem.error) {
        const responseHttpVersion =
          responseItem.httpVersion ?? (s.protocol === 'http2' ? '2.0' : '1.1');

        setHttpContextDialogState({
          requestMention: mention,
          responseMention: createHttpResponseMention({
            id: `replay-response-${sessionId}-${s.currentResponseIndex}`,
            method: parsed.method,
            url: parsed.url,
            statusCode: responseItem.statusCode,
            statusText: responseItem.statusMessage,
            headers: responseItem.headers,
            body: responseItem.body,
            httpVersion: responseHttpVersion,
          }),
        });

        return;
      }

      sendHttpMentionsToChat({
        mentions: [mention],
      });
    },
    [sessionId, appendToInput, sendHttpMentionsToChat],
  );
  const handleRequestSendToReplay = useCallback(
    (content: string) => {
      const s = sessionRef.current;

      if (!s) return;
      const parsed = parseRequestFromText({
        text: content,
        originalUrl: s.request.url,
      });

      if (!parsed) {
        toasterApi.error({
          title: 'Failed to send to replay',
          description: 'Could not parse the request. Check the request format.',
        });

        return;
      }

      openInReplay({
        method: parsed.method,
        url: parsed.url,
        headers: parsed.headers,
        body: parsed.body,
        httpVersion: parsed.httpVersion,
        connectionTarget: s.connectionTarget,
      });
    },
    [openInReplay],
  );
  const handleConnectionTargetChange = useCallback(
    (target: Partial<ConnectionTarget>) => {
      const s = sessionRef.current;

      if (!s) {
        return;
      }

      const nextTarget = { ...s.connectionTarget, ...target };

      updateSessionConnectionTarget(sessionId, target);
      if (!s.syncTargetWithHostHeader) {
        return;
      }

      updateSessionRequest(
        sessionId,
        replaceHostHeader({
          requestText: s.requestData,
          host: formatTargetHostHeader(nextTarget),
        }),
      );
    },
    [sessionId, updateSessionConnectionTarget, updateSessionRequest],
  );
  const handleSyncTargetWithHostHeaderChange = useCallback(
    (syncTargetWithHostHeader: boolean) => {
      const s = sessionRef.current;

      updateSessionSyncTargetWithHostHeader(
        sessionId,
        syncTargetWithHostHeader,
      );
      if (!s || !syncTargetWithHostHeader) {
        return;
      }

      updateSessionRequest(
        sessionId,
        replaceHostHeader({
          requestText: s.requestData,
          host: formatTargetHostHeader(s.connectionTarget),
        }),
      );
    },
    [sessionId, updateSessionRequest, updateSessionSyncTargetWithHostHeader],
  );
  const sandboxUnavailableTooltip = (() => {
    const reason = preRequestSandboxStatus?.reason;

    if (!reason) {
      return tScripts('sandboxUnavailable');
    }

    if (reason.includes('Node.js binary')) {
      return tScripts('sandboxRequiresNode', {
        productName: PRODUCT_DISPLAY_NAME,
      });
    }

    if (reason.includes('sandbox-exec')) {
      return tScripts('sandboxRequiresMacos');
    }

    if (reason.includes('bubblewrap') || reason.includes('bwrap')) {
      return tScripts('sandboxRequiresBubblewrap');
    }

    if (reason.includes('WSL')) {
      return tScripts('sandboxRequiresWsl');
    }

    return tScripts('sandboxUnavailableReason', { reason });
  })();

  if (!session) {
    return (
      <Box h="full" display="flex" alignItems="center" justifyContent="center">
        <Text color="fg.muted">{tSessions('notFound')}</Text>
      </Box>
    );
  }

  const responseHistory = session.responseHistory;
  const currentResponseIndex = session.currentResponseIndex;
  const response = responseHistory[currentResponseIndex] || null;
  const activeCredential: ActiveCredential = session.activeCredential;
  const isRedirect =
    response &&
    [301, 302, 303, 307, 308].includes(response.statusCode) &&
    Object.keys(response.headers).some(
      (key) => key.toLowerCase() === 'location',
    );
  const isSandboxUnavailableWithoutFallback =
    preRequestSandboxStatus?.ready === false &&
    !preRequestSandboxStatus.allowUnsafeFallback;
  const isSandboxUnavailableWithFallback =
    preRequestSandboxStatus?.ready === false &&
    preRequestSandboxStatus.allowUnsafeFallback;
  const scriptTooltipTitle = !session.applicationId
    ? tScripts('selectApplication')
    : isSandboxUnavailableWithoutFallback
      ? sandboxUnavailableTooltip
      : isSandboxUnavailableWithFallback
        ? tScripts('sandboxUnavailableWithFallback')
        : preRequestSandboxStatus
          ? tScripts('runPreRequestScript', {
              sandboxName:
                preRequestSandboxStatus.sandboxName ??
                tScripts('secureSandbox'),
            })
          : tScripts('checkingSandbox');

  return (
    <Box
      h="full"
      w="full"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Box
        borderBottomWidth="1px"
        borderColor="border.default"
        bg="bg.subtle"
        flexShrink={0}
      >
        {/* Row 1: Status, Navigation, Actions */}
        <ReplayToolbar
          responseHistory={responseHistory}
          response={response}
          currentResponseIndex={currentResponseIndex}
          isLoading={isLoading}
          isRedirect={!!isRedirect}
          onPrevious={() => handleNavigateResponse('prev')}
          onNext={() => handleNavigateResponse('next')}
          onReset={handleReset}
          onFollowRedirect={handleFollowRedirect}
          onReplay={handleReplay}
        />

        {/* Row 2: Target, Protocol, Checkboxes */}
        <ReplayOptionsBar
          session={session}
          showHttpsTunnelWarning={
            session.connectionTarget.https && !isHttpsProxySupportEnabled
          }
          scriptTooltipTitle={scriptTooltipTitle}
          onUpdateConnectionTarget={handleConnectionTargetChange}
          onUpdatePreserveContentLength={(val) =>
            updateSessionPreserveContentLength(sessionId, val)
          }
          onUpdateAutoFollowRedirects={(val) =>
            updateSessionAutoFollowRedirects(sessionId, val)
          }
          onUpdateSyncTargetWithHostHeader={
            handleSyncTargetWithHostHeaderChange
          }
          onSelectPreset={setActivePresetId}
        />
      </Box>

      {/* Request/Response Split View */}
      <VStack flex="1" minH={0} gap="0" w="full">
        <Splitter.Root
          defaultSize={replaySplitterSizes.map(({ size }) => size)}
          panels={replaySplitterSizes.map(({ id }) => ({
            id: id.toString(),
          }))}
          orientation="horizontal"
          style={{ flex: 1, overflow: 'hidden' }}
          w="full"
          minW="0"
          onResizeEnd={({ size }) => {
            setReplaySplitterSizes(
              replaySplitterSizes.map((panel, index) => ({
                ...panel,
                size: size[index],
              })),
            );
          }}
        >
          {/* Request Panel */}
          <Splitter.Panel
            id="request"
            borderWidth="0"
            w="full"
            h="full"
            minW="0"
          >
            <Box
              h="full"
              w="full"
              display="flex"
              flexDirection="column"
              minH={0}
              minW="0"
            >
              {(response?.credential || activeCredential) && (
                <HStack
                  px="2"
                  py="1"
                  borderBottomWidth="1px"
                  borderColor="border.subtle"
                  bg="bg.subtle"
                  flexShrink={0}
                >
                  <Badge
                    size="sm"
                    variant="emphasis"
                    icon={<UserIcon size={10} />}
                  >
                    {response?.credential
                      ? `${response.credential.username} (${response.credential.role})`
                      : `${activeCredential?.username} (${activeCredential?.role})`}
                  </Badge>
                </HStack>
              )}
              <Box flex="1" w="full" minW="0" overflow="hidden">
                <ReplayRequestEditor
                  key={sessionId}
                  value={session.requestData}
                  onChange={handleRequestChange}
                  activeCredential={activeCredential}
                  onCredentialApplied={handleCredentialApplied}
                  applicationId={session.applicationId}
                  height="100%"
                  canonicalRequestUrl={session.request.url}
                  onRunAllCredentials={stableHandleRunAllCredentials}
                  onSendToChat={handleRequestSendToChat}
                  onSendToReplay={handleRequestSendToReplay}
                  {...editorProps}
                />
              </Box>
            </Box>
          </Splitter.Panel>

          <Splitter.ResizeTrigger
            id="request:response"
            width="1px"
            minWidth="1px"
            bg="border.subtle"
            _hover={{ bg: 'border.default' }}
          />

          {/* Response Panel */}
          <Splitter.Panel
            id="response"
            borderWidth="0"
            w="full"
            h="full"
            minW="0"
          >
            {responsePanelSession ? (
              <ReplayResponsePanel
                sessionId={sessionId}
                session={responsePanelSession}
                response={response}
                grpcHarDisplay={grpcHarDisplay ?? session?.grpcHarDisplay}
                bodyView={bodyView}
                appendToInput={appendToInput}
                sendHttpMentionsToChat={sendHttpMentionsToChat}
                editorProps={editorProps}
              />
            ) : (
              <Box p="2">
                <Text fontSize="xs" color="fg.muted">
                  Click replay to send the request
                </Text>
              </Box>
            )}
          </Splitter.Panel>
        </Splitter.Root>
      </VStack>
      <SelectHttpContextForChatDialog
        state={httpContextDialogState}
        onClose={handleHttpContextDialogClose}
      />
    </Box>
  );
}

/* ---------- Sub-components ---------- */
const TOOLBAR_ICON_SIZE = 12;
const REPLAY_STATUS_SLOT_WIDTH = '96px';
const REPLAY_NAVIGATION_SLOT_WIDTH = '96px';

function ReplayToolbar({
  responseHistory,
  response,
  currentResponseIndex,
  isLoading,
  isRedirect,
  onPrevious,
  onNext,
  onReset,
  onFollowRedirect,
  onReplay,
}: {
  responseHistory: {
    statusCode: number;
    duration: number;
  }[];
  response: {
    statusCode: number;
    duration: number;
  } | null;
  currentResponseIndex: number;
  isLoading: boolean;
  isRedirect: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onReset: () => void;
  onFollowRedirect: () => void;
  onReplay: () => void;
}) {
  const hasResponseHistory = responseHistory.length > 0;
  const displayIndex = hasResponseHistory ? currentResponseIndex + 1 : 0;
  const totalResponses = responseHistory.length;

  return (
    <HStack p="1" gap="1" alignItems="center" w="full">
      <HStack
        w={REPLAY_STATUS_SLOT_WIDTH}
        minW={REPLAY_STATUS_SLOT_WIDTH}
        gap="1"
        alignItems="center"
        flexShrink={0}
      >
        {response && hasResponseHistory ? (
          <>
            {renderHttpBadge(response.statusCode)}
            <Text fontSize="xs" color="fg.muted" whiteSpace="nowrap">
              {response.duration}ms
            </Text>
          </>
        ) : null}
      </HStack>

      <Box flex="1" />

      <HStack
        gap="0"
        alignItems="center"
        w={REPLAY_NAVIGATION_SLOT_WIDTH}
        minW={REPLAY_NAVIGATION_SLOT_WIDTH}
        justifyContent="center"
        flexShrink={0}
      >
        <IconButton
          aria-label="Previous response"
          size="xs"
          variant="ghost"
          onClick={onPrevious}
          disabled={!hasResponseHistory || currentResponseIndex <= 0}
        >
          <ArrowLeft01Icon size={TOOLBAR_ICON_SIZE} />
        </IconButton>
        <Text
          fontSize="xs"
          color="fg.muted"
          fontFamily="mono"
          minW="50px"
          textAlign="center"
        >
          {hasResponseHistory ? `${displayIndex}/${totalResponses}` : '—/—'}
        </Text>
        <IconButton
          aria-label="Next response"
          size="xs"
          variant="ghost"
          onClick={onNext}
          disabled={
            !hasResponseHistory ||
            currentResponseIndex >= responseHistory.length - 1
          }
        >
          <ArrowRight01Icon size={TOOLBAR_ICON_SIZE} />
        </IconButton>
      </HStack>

      <Box flex="1" />

      <Tooltip title="Reset to original">
        <IconButton
          aria-label="Reset to original"
          size="xs"
          variant="ghost"
          onClick={onReset}
        >
          <Undo02Icon size={TOOLBAR_ICON_SIZE} />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={
          !isRedirect
            ? 'No redirect available'
            : isLoading
              ? 'Loading...'
              : 'Follow redirect'
        }
      >
        <IconButton
          aria-label="Follow redirect"
          size="xs"
          variant="ghost"
          onClick={onFollowRedirect}
          disabled={!isRedirect || isLoading}
        >
          <ArrowRight02Icon size={TOOLBAR_ICON_SIZE} />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={isLoading ? 'Stop Replay' : 'Send Replay Request'}
        subtitle={isLoading ? 'Esc' : `${getPlatformModifierKey()}+Enter`}
      >
        <IconButton
          aria-label={isLoading ? 'Stop replay' : 'Send replay request'}
          size="xs"
          variant="ghost"
          onClick={onReplay}
          colorScheme={isLoading ? 'red' : undefined}
        >
          {isLoading ? (
            <SquareIcon size={TOOLBAR_ICON_SIZE} />
          ) : (
            <PlayIcon size={TOOLBAR_ICON_SIZE} />
          )}
        </IconButton>
      </Tooltip>
    </HStack>
  );
}

function ReplayOptionsBar({
  session,
  showHttpsTunnelWarning,
  scriptTooltipTitle,
  onUpdateConnectionTarget,
  onUpdatePreserveContentLength,
  onUpdateAutoFollowRedirects,
  onUpdateSyncTargetWithHostHeader,
  onSelectPreset,
}: {
  session: {
    connectionTarget: {
      hostname: string;
      port: number;
      https: boolean;
    };
    preserveContentLength?: boolean;
    autoFollowRedirects?: boolean;
    syncTargetWithHostHeader?: boolean;
    applicationId?: string;
  };
  showHttpsTunnelWarning: boolean;
  scriptTooltipTitle: string;
  onUpdateConnectionTarget: (
    target: Partial<{
      hostname: string;
      port: number;
      https: boolean;
    }>,
  ) => void;
  onUpdatePreserveContentLength: (val: boolean) => void;
  onUpdateAutoFollowRedirects: (val: boolean) => void;
  onUpdateSyncTargetWithHostHeader: (val: boolean) => void;
  onSelectPreset: (presetId: string | null) => void;
}) {
  const tOptions = useTranslations('replay.options');
  const hasActiveOverflowOption =
    session.autoFollowRedirects ||
    session.preserveContentLength ||
    session.syncTargetWithHostHeader;

  return (
    <Box
      display="flex"
      flexDirection="column"
      w="100%"
      px="2"
      pb="1.5"
      pt="1"
      gap="1"
      borderTopWidth="1px"
      borderColor="border.subtle"
    >
      <HStack w="100%" alignItems="center" gap="1.5" minW="0">
        <HStack gap="1.5" alignItems="center" minW="0" flex="1">
          <Text fontSize="xs" color="fg.muted" fontWeight="medium">
            Target:
          </Text>
          <Input
            size="xs"
            value={session.connectionTarget.hostname}
            onChange={(e) =>
              onUpdateConnectionTarget({ hostname: e.target.value })
            }
            placeholder="hostname"
            flex="1"
            minW="120px"
            maxW="320px"
            h="24px"
            fontFamily="mono"
            fontSize="xs"
            aria-label="Hostname"
          />
          <Text fontSize="xs" color="fg.muted">
            :
          </Text>
          <PortInput
            port={session.connectionTarget.port}
            onCommit={(port) => onUpdateConnectionTarget({ port })}
          />
        </HStack>

        <HStack gap="1.5" alignItems="center" flexShrink={0}>
          <Tooltip title={tOptions('useHttpsTooltip')}>
            <Box display="inline-flex">
              <Checkbox
                size="sm"
                checked={session.connectionTarget.https}
                onCheckedChange={(details) => {
                  onUpdateConnectionTarget({
                    https: details.checked === true,
                  });
                }}
              >
                <Text fontSize="xs" whiteSpace="nowrap">
                  {tOptions('useHttps')}
                </Text>
              </Checkbox>
            </Box>
          </Tooltip>

          <Box h="16px" w="1px" bg="border.default" />

          {session.applicationId && (
            <Tooltip title={scriptTooltipTitle}>
              <Box display="inline-flex" flexShrink={0}>
                <ScriptPresetsManager
                  applicationId={session.applicationId}
                  onSelectPreset={onSelectPreset}
                />
              </Box>
            </Tooltip>
          )}

          <Menu.Root
            positioning={{ strategy: 'fixed', placement: 'bottom-end' }}
          >
            <Menu.Trigger asChild>
              <IconButton
                aria-label={tOptions('replayOptions')}
                size="xs"
                variant="ghost"
                color="fg.muted"
                position="relative"
              >
                <MoreHorizontalIcon size={12} />
                {hasActiveOverflowOption && (
                  <Box
                    position="absolute"
                    top="1px"
                    right="1px"
                    w="1.5"
                    h="1.5"
                    borderRadius="full"
                    bg="orange.9"
                    borderWidth="1px"
                    borderColor="bg.subtle"
                    pointerEvents="none"
                  />
                )}
              </IconButton>
            </Menu.Trigger>
            <Menu.Positioner>
              <Menu.Content minW="220px" py="1">
                <VStack gap="2" alignItems="stretch" p="2">
                  <Tooltip title={tOptions('syncTargetWithHostTooltip')}>
                    <Box display="inline-flex">
                      <Checkbox
                        size="sm"
                        checked={session.syncTargetWithHostHeader || false}
                        onCheckedChange={(details) => {
                          onUpdateSyncTargetWithHostHeader(
                            details.checked === true,
                          );
                        }}
                      >
                        <Text fontSize="xs" whiteSpace="nowrap">
                          {tOptions('syncTargetWithHost')}
                        </Text>
                      </Checkbox>
                    </Box>
                  </Tooltip>

                  <Tooltip title={tOptions('followRedirectsTooltip')}>
                    <Box display="inline-flex">
                      <Checkbox
                        size="sm"
                        checked={session.autoFollowRedirects || false}
                        onCheckedChange={(details) => {
                          onUpdateAutoFollowRedirects(details.checked === true);
                        }}
                      >
                        <Text fontSize="xs" whiteSpace="nowrap">
                          {tOptions('followRedirects')}
                        </Text>
                      </Checkbox>
                    </Box>
                  </Tooltip>

                  <Tooltip title={tOptions('keepContentLengthTooltip')}>
                    <Box display="inline-flex">
                      <Checkbox
                        size="sm"
                        checked={session.preserveContentLength || false}
                        onCheckedChange={(details) => {
                          onUpdatePreserveContentLength(
                            details.checked === true,
                          );
                        }}
                      >
                        <Text fontSize="xs" whiteSpace="nowrap">
                          {tOptions('keepContentLength')}
                        </Text>
                      </Checkbox>
                    </Box>
                  </Tooltip>
                </VStack>
              </Menu.Content>
            </Menu.Positioner>
          </Menu.Root>
        </HStack>
      </HStack>

      {showHttpsTunnelWarning && (
        <Text fontSize="xs" color="fg.error">
          HTTPS proxy support is disabled in General Settings. HTTPS requests
          are replayed in tunnel mode and may fail with upstream header/protocol
          errors or return limited response details.
        </Text>
      )}
    </Box>
  );
}

const ReplayResponsePanel = React.memo(function ReplayResponsePanel({
  sessionId,
  session,
  response,
  grpcHarDisplay,
  bodyView,
  appendToInput,
  sendHttpMentionsToChat,
  editorProps,
}: {
  sessionId: string;
  session: {
    request: {
      method: string;
      url: string;
    };
    currentResponseIndex: number;
    protocol?: 'http1' | 'http2';
  };
  response: {
    statusCode: number;
    statusMessage: string;
    headers: Record<string, string | string[]>;
    rawHeaders?: string[];
    body: string;
    error?: string;
    httpVersion?: string;
    webGrpcDecodedData?: WebGrpcDecodedData | null;
  } | null;
  grpcHarDisplay?: StoredHarDisplayEntry;
  bodyView: HarGrpcBodyView;
  appendToInput: (text: string) => void;
  sendHttpMentionsToChat: ({
    mentions,
    contextMessage,
  }: {
    mentions: ReturnType<typeof createHttpResponseMention>[];
    contextMessage?: string;
  }) => void;
  editorProps?: ReturnType<typeof useReplayGrpcBodyView>['editorProps'];
}) {
  const responseToolbarSpacer = (
    <Box w="5" h="5" flexShrink={0} aria-hidden visibility="hidden" />
  );
  const formattedResponse = useMemo(() => {
    if (!response || response.error) {
      return '';
    }

    const httpVersion =
      response.httpVersion ?? (session.protocol === 'http2' ? '2.0' : '1.1');
    const fallbackFormattedResponse = formatHttpResponseRaw({
      status: response.statusCode,
      statusMessage: response.statusMessage,
      headers: response.headers,
      rawHeaders: response.rawHeaders,
      body: response.body,
      httpVersion,
    });

    return resolveReplayResponseDisplayContent({
      grpcHarDisplay,
      response: {
        statusCode: response.statusCode,
        statusMessage: response.statusMessage,
        headers: response.headers,
        body: response.body,
        httpVersion,
      },
      bodyView,
      webGrpcDecodedData: response.webGrpcDecodedData,
      fallbackFormattedResponse,
    });
  }, [response, session.protocol, grpcHarDisplay, bodyView]);
  const handleSendToChat = useCallback(
    (content: string, selectedText?: string) => {
      if (!response || response.error) return;
      if (selectedText) {
        appendToInput(selectedText);

        return;
      }

      const httpVersion =
        response.httpVersion ?? (session.protocol === 'http2' ? '2.0' : '1.1');
      const mention = createHttpResponseMention({
        id: `replay-response-${sessionId}-${session.currentResponseIndex}`,
        method: session.request.method,
        url: session.request.url,
        statusCode: response.statusCode,
        statusText: response.statusMessage,
        headers: response.headers,
        body: response.body,
        httpVersion,
      });

      sendHttpMentionsToChat({
        mentions: [mention],
      });
    },
    [response, session, sessionId, appendToInput, sendHttpMentionsToChat],
  );

  return (
    <Box h="full" w="full" display="flex" flexDirection="column" minH={0}>
      {response ? (
        response.error ? (
          <Box p="2">
            <Text color="fg.error" fontSize="xs">
              {response.error}
            </Text>
          </Box>
        ) : (
          <HttpEditor
            value={formattedResponse}
            readOnly
            showViewModeToggle
            {...editorProps}
            toolbarContent={responseToolbarSpacer}
            onSendToChat={handleSendToChat}
          />
        )
      ) : (
        <Box p="2">
          <Text fontSize="xs" color="fg.muted">
            Click replay to send the request
          </Text>
        </Box>
      )}
    </Box>
  );
});
